import { getToolUse } from "@/api";
import { ChatObjectType, chatState } from "@/app/chatState";
import { artifact, artifact_list_item, artifact_revision } from "@/app/typing/artifacts";
import { sourceParent } from "../artifactSource";
import { ChatMessage, ChatMessageBlockText, ChatMessageBlockToolResult, ChatMessageBlockUserAction } from "@/app/typing/chat";
import { tag, tag_list_item, tag_revision } from "@/app/typing/tags";
import { calendar_event, calendar_event_revision } from "@/app/typing/events";
import type { document_list_item } from "@/app/typing/documents";
import { citationsPlugin, storeCitedText } from "@/components/richtext/Citation";
import { PartialString, readPartialJsonString, readPartialNestedJsonString } from "./partialToolInput";
import { splitMarkdownBlocks, streamingCitationTail } from "./markdownBlocks";
import { CitationRegistry, resolveCitationLabels, trimPartialLabelTail } from "./citationLabels";
import { StreamingQuoteContext } from "@/components/richtext/Citation/streaming";
import { ForwardRefEditor } from "@/components/richtext/ForwardRefEditor";
import { codeBlockPlugin, codeMirrorPlugin, headingsPlugin, listsPlugin, MDXEditorMethods, quotePlugin, tablePlugin, thematicBreakPlugin } from "@mdxeditor/editor";
import { observer } from "mobx-react-lite";
import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import ChatTagList from "./chatWindowComponents/chatTagList";
import ChatArtifactList from "./chatWindowComponents/chatArtifactList";
import ChatArtifact from "./chatWindowComponents/chatArtifact";
import ChatTag from "./chatWindowComponents/chatTag";
import ChatEventItem from "./chatWindowComponents/chatEvent";
import ChatDocument from "./chatWindowComponents/chatDocument";
import ChatEventList from "./chatWindowComponents/chatEventList";
import { appState } from "@/app/appState";
import { ChatEntryList } from "./chatWindowComponents/chatEntryList";
import { EntryItem, ToolBox } from "@/components/UI/tools";
import { toolFoldoutMocks } from "./toolFoldoutMocks";

export const TextMessageDisplay = (params: { contentClass: string; content: string; ref?: React.RefObject<MDXEditorMethods | null> }) => {
    const collapsed = false;
    const no_codeblock_content = params.content.replace(/```/g, "")

    return (
        <ForwardRefEditor
            ref={params.ref}
            plugins={[headingsPlugin(), listsPlugin(), quotePlugin(), thematicBreakPlugin(), citationsPlugin(), codeBlockPlugin({ defaultCodeBlockLanguage: '' }), codeMirrorPlugin({ codeBlockLanguages: { '': "Plain Text" } }), tablePlugin()]}
            readOnly={true}
            markdown={no_codeblock_content}
            className={`!w-full block ${collapsed ? " max-h-[2rem] overflow-hidden" : " h-fit"}`}
            suppressHtmlProcessing={true}
            contentEditableClassName={params.contentClass}
        />
    );
};

export const SkillChip = observer(({ skillKey, label }: { skillKey: string; label?: string }) => {
    const title = label || appState.t(`skill.${skillKey}.command`);
    return (
        <div className="flex flex-row justify-center w-full px-2 py-1">
            <span className="skill-chip">
                /{title}
            </span>
        </div>
    );
});

type ResultBasic = {
    content: any,
}

type ToolResultFoldoutType = ResultBasic & {
    type: "tool_result"
    name?: "cruq_tool" | "validate_citation" | "get_agent_tool" | "set_selected_objects_tool" | "modify_user_preferences_tool" | "get_event_summary_tool" | "read_summary_tool" | "read_chunk_tool" | "current_time_tool" | "get_conversation_constants" | "skills_tool"
    intent?: string,
    tool_id: string,
    entity_type?: string,
    operation?: string,
    is_error?: boolean,
}

type UserActionFoldoutType = ResultBasic & {
    type: "user_action"
    action: ChatMessageBlockUserAction["action"]
    object_type: ChatObjectType,
    object_id: number
}

type ErrorFoldoutType = ResultBasic & {
    type: "error"
}

type ResultFoldoutContentType = ToolResultFoldoutType | UserActionFoldoutType | ErrorFoldoutType

// A foldout is rendered from a record stored on a message: a tool_result for a tool_use entry,
// or the user_action entry itself.
type FoldoutRef = { message_id: number, name: string, id: string }

/** Label shown next to streamed intent, matching completed foldout EntryItem names. */
function cruqToolHeader(entityType?: string | null, operation?: string | null): string | null {
    if (!entityType || !operation) return null;
    switch (entityType) {
        case "artifact":
            switch (operation) {
                case "create": return appState.t("chat.tool.draftArtifact");
                case "update": return appState.t("chat.tool.artifactUpdate");
                case "query": return appState.t("chat.tool.artifactQuery");
                case "read": return appState.t("chat.tool.readingArtifact");
                default: return appState.t("chat.tool.entityOperation", { entity: appState.t("chat.objectType.artifact"), operation });
            }
        case "tag":
            switch (operation) {
                case "create": return appState.t("chat.tool.draftTag");
                case "update": return appState.t("chat.tool.tagUpdate");
                case "query": return appState.t("chat.tool.tagQuery");
                default: return appState.t("chat.tool.entityOperation", { entity: appState.t("chat.objectType.tag"), operation });
            }
        case "event":
            switch (operation) {
                case "create": return appState.t("chat.tool.event");
                case "update": return appState.t("chat.tool.eventUpdate");
                case "query": return appState.t("chat.tool.eventQuery");
                case "read": return appState.t("chat.tool.eventSummaryRead");
                default: return appState.t("chat.tool.entityOperation", { entity: appState.t("chat.objectType.event"), operation });
            }
        default:
            return null;
    }
}

function runningToolLabel(opts: {
    toolName: string;
    entityType?: PartialString | null;
    operation?: PartialString | null;
    intent?: PartialString | null;
}): { label: string; intent?: string } {
    const header = opts.toolName === "cruq_tool"
        ? (opts.entityType?.complete && opts.operation?.complete
            ? cruqToolHeader(opts.entityType.value, opts.operation.value)
            : null)
        : opts.toolName.replace(/_tool$/, "").replace(/_/g, " ");

    const intentText = opts.intent?.value?.trim() ? opts.intent.value : undefined;
    return { label: header ?? "", intent: intentText };
}

export type ToolRunStatus = "streaming" | "success" | "error";

const TOOL_STATUS_CLASS: Record<ToolRunStatus, string> = {
    streaming: "",
    success: "tool-status-success",
    error: "tool-status-error",
};

/** One filled circle for every state; only colour (and the running pulse) changes. */
export function ToolStatusIcon({ status }: { status: ToolRunStatus }) {
    return (
        <span className={`tool-status ${TOOL_STATUS_CLASS[status]}`} aria-hidden>
            <svg className={`w-full h-full ${status === "streaming" ? "status-pulse" : ""}`} viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="6" />
            </svg>
        </span>
    );
}

/** `bug_report_tool` -> `Bug report`. */
const humanizeToolName = (name?: string) => {
    const words = (name ?? "").replace(/_tool$/, "").replace(/_/g, " ").trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : appState.t("chat.tool.operationCompleted");
};

/** Small status foldout: icon + `label - intent`, used for non-object tool results. */
export function StatusToolBox({
    status,
    label,
    intent,
    children,
}: {
    status: ToolRunStatus;
    label: string;
    intent?: string;
    children?: ReactNode;
}) {
    const intentText = intent?.trim();
    const text = intentText ? (label ? `${label} - ${intentText}` : intentText) : label;
    return (
        <ToolBox is_small={true}>
            <span className="flex items-center min-w-0">
                <ToolStatusIcon status={status} />
                <span className="toolbox-compact-text" title={text || undefined}>{text || <span className="inline-block min-w-[0.5rem] opacity-0 select-none">.</span>}</span>
            </span>
            {children}
        </ToolBox>
    );
}

// The tool_result for a tool_use is stored on a later message, so we scan the conversation for it.
function findToolResultRef(tool_use_id: string): FoldoutRef | null {
    for (const block of chatState.conversation) {
        for (const message of block.content) {
            if (!message.id || !Array.isArray(message.content)) continue

            const result = message.content.find(item => item.type == "tool_result" && item.id == tool_use_id) as ChatMessageBlockToolResult | undefined

            if (result) return { message_id: message.id, name: result.name, id: result.id }
        }
    }
    return null
}


export const ToolMessageDisplay = observer((props: { msg: ChatMessage, entry: any, partial_tool_input?: string, streaming?: boolean }) => {
    const frozenInput: string | undefined = props.entry?.partial_input;
    const params = props.streaming ? { ...props, partial_tool_input: frozenInput ?? chatState.streamingContent } : props;
    const [foldoutContent, setFoldoutContent] = useState<ResultFoldoutContentType | undefined>();
    const id = params.msg.conversation_id

    const is_tool_use = params.entry?.type === "tool_use"
    const tool_use = params.entry as { type: "tool_use", id: string, name: string, input?: any }

    // Parse from the live partial/complete tool JSON each render so foldouts don't
    // miss intent on the first paint after getToolUse resolves.
    const intent = params.partial_tool_input
        ? readPartialJsonString(params.partial_tool_input, "intent")
        : null;
    const thinking = params.partial_tool_input
        ? readPartialNestedJsonString(params.partial_tool_input, "input", "content")
        : null;
    const entityType = params.partial_tool_input
        ? readPartialJsonString(params.partial_tool_input, "type")
        : null;
    const operation = params.partial_tool_input
        ? readPartialJsonString(params.partial_tool_input, "operation")
        : null;

    let foldout_ref: FoldoutRef | null = null

    if (is_tool_use) {
        // Null while the tool is still running; the observer re-renders once the result lands.
        foldout_ref = findToolResultRef(tool_use.id) ?? null
    } else if (params.msg.id) {
        // A user_action is already complete, and its record lives on this very message.
        foldout_ref = { message_id: params.msg.id, name: params.entry.name, id: params.entry.id }
    }



    const loadFoldoutContent = async () => {
        if (!foldout_ref || (foldout_ref.message_id ?? 0) <= 0) return;

        const isStale = chatState._makeStaleGuard();

        if (id && foldout_ref.message_id) {
            // Harness override: skip the API when a foldout payload was registered for this tool id.
            const mock = toolFoldoutMocks.get(foldout_ref.id);
            const fetchPromise = mock
                ? Promise.resolve({ data: { content: structuredClone(mock) }, error: undefined as any })
                : getToolUse({ body: { msg_id: foldout_ref.message_id, conversation_id: id, tool_id: foldout_ref.id } });

            fetchPromise.then(async (response) => {
                if (isStale()) return;
                if (response.error) {
                    setFoldoutContent({ type: "error", content: appState.t("chat.tool.error") })
                    return;
                }

                const data = response.data as any;
                if ("content" in data && data.content == "deleted") {
                    setFoldoutContent({ type: "error", content: `deleted` })
                    return;
                }
                const content: ResultFoldoutContentType | null = data?.content ?? null;
                if (!content || typeof content !== "object") {
                    setFoldoutContent({ type: "error", content: appState.t("chat.tool.error") });
                    return;
                }

                const q = content

                switch (q.type) {
                    case "tool_result":
                        const tool_id = q.tool_id

                        switch (q.name) {
                            case "modify_user_preferences_tool":
                                appState.getPreferences();
                                break;
                            case "skills_tool":
                                if (!q.is_error && q.content?.saved) appState.getUserSkills();
                                break;
                            case "get_agent_tool":
                                break
                            case "validate_citation":
                                // No state update needed, but valid tool
                                break
                            case "cruq_tool":
                                if (q.is_error) break; // No state update for errors
                                switch (q.entity_type) {
                                    case "artifact":
                                        switch (q.operation) {
                                            case "read":
                                            case "query":
                                                if (q.content instanceof Array && q.content.length > 0) {
                                                    const x: artifact_list_item[] = q.content as artifact_list_item[]
                                                    x.map(y => {
                                                        chatState.chatObjects.update([{ key: { type: "artifact", id: y.id }, value: { parent: sourceParent(y), created_at: y.created_at, id: y.id, label: y.variable_name, msgId: params.msg.id, artifact: y, type: "artifact" } }])
                                                    })
                                                }
                                                break
                                            case "create":
                                                if (q.content.artifact) {
                                                    const x: artifact = q.content.artifact as artifact
                                                    chatState.chatObjects.update([{ key: { type: "artifact", id: x.id }, value: { parent: sourceParent(x), created_at: x.created_at, id: x.id, label: x.variable_name, msgId: params.msg.id, artifact: x, type: "artifact" } }])
                                                }
                                                break
                                            case "update":
                                                if (q.content?.id) {
                                                    const x: artifact_revision = { ...q.content, tool_id } as artifact_revision
                                                    let obj = chatState.chatObjects.get({ type: "artifact", id: x.artifact_id })
                                                    if (!(obj && obj.artifact && 'content' in obj.artifact)) {
                                                        await chatState.getObjectByKey({ type: "artifact", id: x.artifact_id })
                                                        obj = chatState.chatObjects.get({ type: "artifact", id: x.artifact_id })
                                                    }
                                                    chatState.applyIncomingRevision({ type: "artifact", id: x.artifact_id }, x, params.msg.id,
                                                        { variable_name: obj?.temp_obj?.variable_name, content: undefined, tag_ids: undefined })
                                                }
                                                break
                                        }
                                        break
                                    case "tag":
                                        switch (q.operation) {
                                            case "query":
                                                if (q.content instanceof Array && q.content.length > 0) {
                                                    const x: tag_list_item[] = q.content as tag_list_item[]
                                                    x.map(y => {
                                                        const curr = chatState.chatObjects.get({ type: "tag", id: y.id })
                                                        if (!(curr && curr.type == "tag" && curr.tag && 'description' in curr.tag))
                                                            chatState.chatObjects.update([{ key: { type: "tag", id: y.id }, value: { created_at: y.created_at, id: y.id, label: y.name, msgId: params.msg.id, tag: y, type: "tag" } }])
                                                        else {
                                                            chatState.chatObjects.update([{ key: { type: "tag", id: y.id }, value: { msgId: params.msg.id } }])
                                                        }
                                                    })
                                                }
                                                break
                                            case "create":
                                                if (q.content?.tag) {
                                                    const x: tag = q.content.tag as tag
                                                    chatState.chatObjects.update([{ key: { type: "tag", id: x.id }, value: { created_at: x.created_at, id: x.id, label: x.name, msgId: params.msg.id, tag: x, type: "tag" } }])
                                                } else if (q.content?.tag_id) {
                                                    await chatState.getObjectByKey({ type: "tag", id: q.content.tag_id })
                                                    chatState.chatObjects.update([{ key: { type: "tag", id: q.content.tag_id }, value: { msgId: params.msg.id } }])
                                                }
                                                break
                                            case "update":
                                                if (q.content.id) {
                                                    const x: tag_revision = { ...q.content, tool_id } as tag_revision
                                                    let obj = chatState.chatObjects.get({ type: "tag", id: x.tag_id })
                                                    if (!(obj && obj.tag && 'description' in obj.tag)) {
                                                        await chatState.getObjectByKey({ type: "tag", id: x.tag_id })
                                                        obj = chatState.chatObjects.get({ type: "tag", id: x.tag_id })
                                                    }
                                                    chatState.applyIncomingRevision({ type: "tag", id: x.tag_id }, x, params.msg.id,
                                                        { name: obj?.temp_obj?.name, description: undefined, scope: undefined, permission_type: undefined, tpr_organisation_ids: undefined, attributes: undefined })
                                                }
                                                break
                                        }
                                        break
                                    case "event":
                                        switch (q.operation) {
                                            case "read":
                                            case "query":
                                                if (q.content instanceof Array && q.content.length > 0) {
                                                    const events = q.content as calendar_event[]
                                                    events.forEach(ev => {
                                                        const curr = chatState.chatObjects.get({ type: "event", id: ev.id })
                                                        if (!curr) {
                                                            chatState.chatObjects.update([{
                                                                key: { type: "event", id: ev.id },
                                                                value: { created_at: ev.start_time, id: ev.id, label: ev.title, msgId: params.msg.id, event: ev, type: "event" }
                                                            }])
                                                        } else {
                                                            chatState.chatObjects.update([{ key: { type: "event", id: ev.id }, value: { msgId: params.msg.id } }])
                                                        }
                                                    })
                                                }
                                                break
                                            case "create":
                                                if (q.content?.event) {
                                                    const x: calendar_event = q.content.event as calendar_event
                                                    chatState.chatObjects.update([{ key: { type: "event", id: x.id }, value: { created_at: x.start_time, id: x.id, label: x.title, msgId: params.msg.id, event: x, type: "event" } }])
                                                }
                                                break
                                            case "update":
                                                if (q.content?.id) {
                                                    const x: calendar_event_revision = { ...q.content, tool_id } as calendar_event_revision
                                                    let obj = chatState.chatObjects.get({ type: "event", id: x.event_id })
                                                    if (!(obj && obj.event && 'title' in obj.event)) {
                                                        await chatState.getObjectByKey({ type: "event", id: x.event_id })
                                                        obj = chatState.chatObjects.get({ type: "event", id: x.event_id })
                                                    }
                                                    chatState.applyIncomingRevision({ type: "event", id: x.event_id }, x, params.msg.id,
                                                        { title: obj?.temp_obj?.title, description: undefined, location: undefined, start_time: undefined, end_time: undefined, all_day: undefined, timezone: undefined, tag_id: undefined })
                                                }
                                                break
                                        }
                                        break
                                }
                                break

                        }
                        break
                    case "user_action":
                        switch (q.object_type) {
                            case "artifact":
                                switch (q.action) {
                                    case "create":
                                    case "user_changes":
                                    case "update":
                                        if (q.content.artifact) {
                                            const x: artifact = q.content.artifact as artifact
                                            chatState.chatObjects.update([{ key: { type: "artifact", id: x.id }, value: { parent: sourceParent(x), created_at: x.created_at, id: x.id, label: x.variable_name, msgId: params.msg.id, artifact: x, type: "artifact" } }])
                                        }
                                        break
                                    case "revision":
                                        if (q.content?.id) {

                                            const x: artifact_revision = { ...q.content } as artifact_revision
                                            let obj = chatState.chatObjects.get({ type: "artifact", id: x.artifact_id })
                                            if (!(obj && obj.artifact && 'content' in obj.artifact)) {
                                                await chatState.getObjectByKey({ type: "artifact", id: x.artifact_id })
                                                obj = chatState.chatObjects.get({ type: "artifact", id: x.artifact_id })
                                            }
                                            chatState.applyIncomingRevision({ type: "artifact", id: x.artifact_id }, x, params.msg.id,
                                                { variable_name: obj?.temp_obj?.variable_name, content: undefined, tag_ids: undefined })
                                        }
                                        break
                                }
                                break
                            case "tag":
                                switch (q.action) {
                                    case "create":
                                        if (q.content?.tag) {
                                            const x: tag = q.content.tag as tag
                                            chatState.chatObjects.update([{ key: { type: "tag", id: x.id }, value: { created_at: x.created_at, id: x.id, label: x.name, msgId: params.msg.id, tag: x, type: "tag" } }])
                                        } else if (q.object_id) {
                                            await chatState.getObjectByKey({ type: "tag", id: q.object_id })
                                            chatState.chatObjects.update([{ key: { type: "tag", id: q.object_id }, value: { msgId: params.msg.id } }])
                                        }
                                        break
                                    case "revision":
                                        if (q.content?.id) {
                                            const x: tag_revision = { ...q.content } as tag_revision
                                            let obj = chatState.chatObjects.get({ type: "tag", id: x.tag_id })
                                            if (!(obj && obj.tag && 'description' in obj.tag)) {
                                                await chatState.getObjectByKey({ type: "tag", id: x.tag_id })
                                                obj = chatState.chatObjects.get({ type: "tag", id: x.tag_id })
                                            }
                                            chatState.applyIncomingRevision({ type: "tag", id: x.tag_id }, x, params.msg.id,
                                                { name: obj?.temp_obj?.name, description: undefined, scope: undefined, permission_type: undefined, tpr_organisation_ids: undefined, attributes: undefined })
                                        }
                                        break
                                }
                                break
                            case "event":
                                switch (q.action) {
                                    case "create":
                                        if (q.content?.event) {
                                            const x: calendar_event = q.content.event as calendar_event
                                            chatState.chatObjects.update([{ key: { type: "event", id: x.id }, value: { created_at: x.created_at, id: x.id, label: x.title, msgId: params.msg.id, event: x, type: "event" } }])
                                        } else if (q.object_id) {
                                            await chatState.getObjectByKey({ type: "event", id: q.object_id })
                                            chatState.chatObjects.update([{ key: { type: "event", id: q.object_id }, value: { msgId: params.msg.id } }])
                                        }
                                        break
                                }
                                break
                            case "document":
                                switch (q.action) {
                                    case "create":
                                        if (q.content?.document) {
                                            const x: document_list_item = q.content.document as document_list_item
                                            chatState.chatObjects.update([{ key: { type: "document", id: x.id }, value: { created_at: x.created_at, id: x.id, label: x.variable_name, msgId: params.msg.id, document: x, type: "document", unsaved_changes: false } }])
                                        } else if (q.object_id) {
                                            await chatState.getObjectByKey({ type: "document", id: q.object_id })
                                            chatState.chatObjects.update([{ key: { type: "document", id: q.object_id }, value: { msgId: params.msg.id } }])
                                        }
                                        break
                                }
                                break
                        }
                        break
                }


                setFoldoutContent(content);
            }).catch((error) => {
                console.error("Error fetching tool use result:", error);
                setFoldoutContent({ type: "error", content: appState.t("chat.tool.error") });
            }).catch((error) => {
                console.error("Error fetching tool use result:", error);
                setFoldoutContent({ type: "error", content: appState.t("chat.tool.error") });
            });
        }
    }

    const renderFoldout = (q: ResultFoldoutContentType, ref: FoldoutRef, intentText?: string) => {
        if (!q.content)
            return (
                <StatusToolBox status="error" label={appState.t("chat.tool.error")} intent={intentText} />
            )
        else if (q.content == "deleted") {
            return (
                <StatusToolBox status="error" label={appState.t("chat.tool.deleted")} intent={intentText} />
            )
        }
        if (q.content?.length == 0) {
            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />;
        }

        // TODO - change the way that i load the latest action element. Instead of current method, we should make a method i chatState, that goes over conversation in reverse, and stops at first element of interest.
        switch (q.type) {
            case "error":
                return <StatusToolBox status="error" label={typeof q.content === "string" ? q.content : appState.t("chat.tool.error")} intent={intentText} />
            case "tool_result":
                if (q.is_error) {
                    return (
                        <StatusToolBox
                            status="error"
                            label={typeof q.content === "string" ? q.content : appState.t("chat.tool.error")}
                            intent={intentText}
                        />
                    )
                }

                switch (q.name) {
                    case "modify_user_preferences_tool":
                        return <StatusToolBox status="success" label={appState.t("chat.tool.userPreferencesUpdated")} intent={intentText} />
                    case "get_agent_tool":
                        if (q.content) {
                            return <StatusToolBox status="success" label={`Set agent ${q.content}`} intent={intentText} />
                        }
                        return <StatusToolBox status="success" label={appState.t("chat.tool.operationCompleted")} intent={intentText} />
                    case "set_selected_objects_tool":
                        if (q.content) {
                            return <StatusToolBox status="success" label={appState.t("chat.tool.setSelectedObjects")} intent={intentText} />
                        }
                        return <StatusToolBox status="success" label={appState.t("chat.tool.operationCompleted")} intent={intentText} />
                    case "validate_citation":
                        return <StatusToolBox status="success" label={appState.t("chat.tool.verifiedCitation")} intent={intentText} />
                    case "cruq_tool":
                        if (q.is_error) {
                            return <StatusToolBox status="error" label={appState.t("chat.tool.error")} intent={intentText} />
                        }
                        if (q.content && typeof q.content === 'object' && 'instructions' in q.content) {
                            return <StatusToolBox status="success" label={appState.t("chat.tool.readingInstructions")} intent={intentText} />
                        }
                        switch (q.entity_type) {
                            case "artifact":
                                switch (q.operation) {
                                    case "query":
                                        if (q.content instanceof Array && q.content.length > 0) {
                                            return <ToolBox is_small={false}><ChatArtifactList artifacts={q.content as artifact_list_item[]} tool_use_id={ref.id} intent={intentText} /></ToolBox>
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                    case "create":
                                        if (q.content.artifact) {

                                            return <ToolBox is_small={false} lift={true} draft={q.content.artifact.is_draft}>
                                                <ChatArtifact reload={loadFoldoutContent} tool_id={ref.id} objKey={{ type: "artifact", id: q.content.artifact.id as number }} intent={intentText} />
                                            </ToolBox>
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                    case "update":
                                        if (q.content?.id) {
                                            console.log("FOLDOUT CONTENT", q);
                                            const isDraft = chatState.chatObjects.get({ type: "artifact", id: q.content.artifact_id })?.artifact.is_draft ?? false
                                            return <ToolBox is_small={false} lift={true} draft={isDraft}>
                                                <ChatArtifact reload={loadFoldoutContent} revision_id={q.content.id} objKey={{ type: "artifact", id: q.content.artifact_id as number }} tool_id={ref.id} intent={intentText} />
                                            </ToolBox>
                                        } else if (q.content?.artifact_id) {
                                            return <StatusToolBox status="error" label={appState.t("chat.tool.revisionInvalid")} intent={intentText} />
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                    case "read":
                                        return <StatusToolBox status="success" label={appState.t("chat.tool.readingArtifact")} intent={intentText} />
                                }
                                return
                            case "tag":
                                switch (q.operation) {
                                    case "query":
                                        if (q.content instanceof Array && q.content.length > 0) {
                                            return <ToolBox is_small={false}><ChatTagList tag_list={q.content as tag_list_item[]} tool_use_id={ref.id} intent={intentText} /></ToolBox>
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                    case "create":
                                        if (q.content.tag) {
                                            return <ToolBox is_small={false}>
                                                <ChatTag reload={loadFoldoutContent} objKey={{ type: "tag", id: q.content.tag.id }} tool_id={ref.id} intent={intentText} />
                                            </ToolBox>
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                    case "update":
                                        if (q.content.id) {
                                            return <ToolBox is_small={false}>
                                                <ChatTag reload={loadFoldoutContent} revision_id={q.content.id} objKey={{ type: "tag", id: q.content.tag_id }} tool_id={ref.id} intent={intentText} />
                                            </ToolBox>
                                        } else if (q.content.tag_id) {
                                            return <StatusToolBox status="error" label={appState.t("chat.tool.tagRevisionInactive", { id: q.content.tag_id })} intent={intentText} />
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                }
                                return
                            case "event":
                                switch (q.operation) {
                                    case "query":
                                        if (q.content instanceof Array && q.content.length > 0) {
                                            return <ToolBox is_small={false}>
                                                <ChatEventList events={q.content as calendar_event[]} tool_use_id={ref.id} intent={intentText} />
                                            </ToolBox>
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noEvents")} intent={intentText} />
                                        }
                                    case "read":
                                        return <StatusToolBox status="success" label={appState.t("chat.tool.eventSummaryRead")} intent={intentText} />
                                    case "create":
                                        if (q.content?.event?.id) {
                                            return <ToolBox is_small={false} lift={true}>
                                                <ChatEventItem objKey={{ type: "event", id: q.content.event.id }} reload={loadFoldoutContent} intent={intentText} />
                                            </ToolBox>
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                    case "update":
                                        if (q.content?.id) {
                                            return <ToolBox is_small={false} lift={true}>
                                                <ChatEventItem objKey={{ type: "event", id: q.content.event_id }} reload={loadFoldoutContent} revision_id={q.content.id} tool_id={ref.id} intent={intentText} />
                                            </ToolBox>
                                        } else if (q.content?.event_id) {
                                            return <StatusToolBox status="error" label={appState.t("chat.tool.revisionInvalid")} intent={intentText} />
                                        } else {
                                            return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                        }
                                }
                                return
                            case "memory":
                                return <StatusToolBox status="success" label={appState.t("chat.tool.readingHistory")} intent={intentText} />
                            default:
                                return <StatusToolBox status="success" label={appState.t("chat.tool.operationCompleted")} intent={intentText} />
                        }

                    case "get_event_summary_tool":
                        return <StatusToolBox status="success" label={appState.t("chat.tool.eventSummaryRead")} intent={intentText} />
                    case "read_summary_tool":
                        return <StatusToolBox status="success" label={appState.t("chat.tool.readSessionSummary")} intent={intentText} />
                    case "read_chunk_tool":
                        return <StatusToolBox status="success" label={appState.t("chat.tool.readingContext")} intent={intentText} />
                    case "current_time_tool":
                        return <StatusToolBox status="success" label={appState.t("chat.tool.readCurrentTime")} intent={intentText} />
                    case "get_conversation_constants":
                        return <StatusToolBox status="success" label={appState.t("chat.tool.readConversationContext")} intent={intentText} />
                    case "skills_tool":
                        if (q.content?.saved && q.content.skill) {
                            const labelKey = q.content.operation === "updated" ? "chat.tool.skillUpdated" : "chat.tool.skillCreated";
                            return <StatusToolBox status="success" label={appState.t(labelKey, { skill: q.content.command || q.content.skill })} intent={intentText} />
                        }
                        return <StatusToolBox status="success" label={appState.t("chat.tool.operationCompleted")} intent={intentText} />
                    case undefined:
                        return <StatusToolBox status="success" label={appState.t("chat.tool.readingInstructions")} intent={intentText} />
                    default:
                        return <StatusToolBox status="success" label={humanizeToolName(q.name)} intent={intentText} />;
                }
            case "user_action":


                switch (q.object_type) {
                    case "artifact":
                        switch (q.action) {
                            case "create":
                            case "user_changes":
                            case "update":
                                if (q.content.artifact) {
                                    return <ToolBox
                                        is_small={false} lift={true} user={true} draft={q.content.artifact.is_draft}>
                                        <ChatArtifact
                                            reload={loadFoldoutContent}
                                            objKey={{ type: "artifact", id: q?.content?.artifact?.id as number }}
                                            intent={intentText}
                                        />
                                    </ToolBox>;
                                } else {
                                    return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />;
                                }
                            case "revision":

                                if (q.content?.id) {
                                    return <ToolBox
                                        is_small={false} lift={true}>
                                        <ChatArtifact
                                            reload={loadFoldoutContent}
                                            revision_id={q.content?.id}

                                            objKey={{ type: "artifact", id: q.content.artifact_id as number }}
                                            tool_id={ref.id}
                                            intent={intentText}
                                        />
                                    </ToolBox>;
                                } else if (q.content?.artifact_id) {
                                    return <StatusToolBox status="error" label={appState.t("chat.tool.revisionInvalid")} intent={intentText} />
                                } else {
                                    return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />;
                                }
                        }
                        break
                    case "event":
                        switch (q.action) {
                            case "create":
                                if (q.content?.event) {
                                    return <ToolBox is_small={false} lift={true} user={true}>
                                        <ChatEventItem
                                            reload={loadFoldoutContent}
                                            objKey={{ type: "event", id: q.content.event.id }}
                                            intent={intentText}
                                        />
                                    </ToolBox>
                                } else {
                                    return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                }
                        }
                        break
                    case "document":
                        switch (q.action) {
                            case "create":
                                if (q.content?.document?.id) {
                                    return <ToolBox is_small={false} lift={true} user={true}>
                                        <ChatDocument
                                            reload={loadFoldoutContent}
                                            objKey={{ type: "document", id: q.content.document.id }}
                                            intent={intentText}
                                        />
                                    </ToolBox>
                                } else {
                                    return <StatusToolBox status="success" label={appState.t("chat.tool.noResults")} intent={intentText} />
                                }
                        }
                        break
                }
        }
    };


    const foldout_key = foldout_ref ? `${foldout_ref.message_id}:${foldout_ref.id}` : null

    useEffect(() => {
        if (!foldout_key) return          // no record to read yet
        if (foldoutContent) return  // already loaded

        loadFoldoutContent();
    }, [foldout_key, foldoutContent]);


    // Loaded: render the real foldout. Intent is folded into the EntryItem name
    // (`draft artifact - <intent>`), matching the running-tool layout.
    if (foldout_ref && foldoutContent) {
        const intentText = intent?.value?.trim() ? intent.value : undefined;
        return renderFoldout(foldoutContent, foldout_ref, intentText) ?? null
    }

    // Still running or still loading: stable-height box; header + intent fill in as they stream.
    if (is_tool_use) {
        const { label, intent: intentText } = runningToolLabel({
            toolName: tool_use.name,
            entityType,
            operation,
            intent,
        });
        return (
            <StatusToolBox status="streaming" label={label} intent={intentText}>
                {thinking?.value ? (
                    <span className="tool-thinking">
                        {thinking.value.slice(-240)}
                    </span>
                ) : null}
            </StatusToolBox>
        )
    }

    // A user_action always has its record on hand, so there is nothing to fall back to.
    return null
});



function processTextCitations(
    text: string,
    msgId: number | undefined,
): string {
    // ${A:artifact_id, start_line, end_line, "cited_text"} artifact citations are now parsed
    // directly by citationsPlugin — we only run the preload side-effects here and pass text through.
    // LEGACY-CONVERT: previously transformed to [cite:artifact:...] for the plugin.
    // TODO: Remove this block once preload logic moves to citation-click/hover handlers.
    // eslint-disable-next-line no-useless-escape
    const rA = /\$\{A:([0-9]+),\s*([0-9]+),\s*([0-9]+),\s*"((?:[^"\\}]|\\.|"(?!}))*)"\}/g;
    text = text.replace(rA, (match, artifact_id, _start_line, _end_line, _cited_text) => {
        const id = parseInt(artifact_id);
        chatState.chatObjects.update([{ key: { type: "artifact", id }, value: { msgId } }]);
        if (chatState.chatObjects.get({ type: "artifact", id }) === undefined) {
            chatState.getObjectByKey({ type: "artifact", id });
        }
        // LEGACY-CONVERT: const citedTextRef = storeCitedText(_cited_text.replace(/\\"/g, '"'));
        // LEGACY-CONVERT: return `[cite:artifact:${id}:${_start_line}:${_end_line}:${citedTextRef}]`;
        return match; // pass through; citationsPlugin handles ${A:...} directly
    });

    // ${E:event_id, start_line, end_line, "cited_text"} event citations are now parsed
    // directly by citationsPlugin — we only run the preload side-effects here and pass text through.
    // LEGACY-CONVERT: previously transformed to [cite:event:...] for the plugin.
    // TODO: Remove this block once preload logic moves to citation-click/hover handlers.
    // eslint-disable-next-line no-useless-escape
    const rE = /\$\{E:([0-9]+),\s*([0-9]+),\s*([0-9]+),\s*"((?:[^"\\}]|\\.|"(?!}))*)"\}/g;
    text = text.replace(rE, (match, event_id, _start_line, _end_line, _cited_text) => {
        const id = parseInt(event_id);
        chatState.chatObjects.update([{ key: { type: "event", id }, value: { msgId } }]);
        if (chatState.chatObjects.get({ type: "event", id }) === undefined) {
            chatState.getObjectByKey({ type: "event", id });
        }
        // LEGACY-CONVERT: const citedTextRef = storeCitedText(_cited_text.replace(/\\"/g, '"'));
        // LEGACY-CONVERT: return `[cite:event:${id}:${_start_line}:${_end_line}:${citedTextRef}]`;
        return match; // pass through; citationsPlugin handles ${E:...} directly
    });

    // ${D:document_id, start_line, end_line, "cited_text"} document citations — same
    // preload-and-pass-through treatment as artifacts and events.
    // eslint-disable-next-line no-useless-escape
    const rD = /\$\{D:([0-9]+),\s*([0-9]+),\s*([0-9]+),\s*"((?:[^"\\}]|\\.|"(?!}))*)"\}/g;
    text = text.replace(rD, (match, document_id) => {
        const id = parseInt(document_id);
        chatState.chatObjects.update([{ key: { type: "document", id }, value: { msgId } }]);
        if (chatState.chatObjects.get({ type: "document", id }) === undefined) {
            chatState.getObjectByKey({ type: "document", id });
        }
        return match; // pass through; citationsPlugin handles ${D:...} directly
    });

    // Link references (no cited_text): ${A:1}, ${A:1, 34, 50}, ${E:2}, ${D:3}.
    // Run the same preload side-effects; citationsPlugin renders them as inline markers.
    // These deliberately do not match full citations (which have `, "text"` before `}`).
    // eslint-disable-next-line no-useless-escape
    const rAf = /\$\{A:([0-9]+)(?:,\s*[0-9]+,\s*[0-9]+)?\}/g;
    text = text.replace(rAf, (match, artifact_id) => {
        const id = parseInt(artifact_id);
        chatState.chatObjects.update([{ key: { type: "artifact", id }, value: { msgId } }]);
        if (chatState.chatObjects.get({ type: "artifact", id }) === undefined) {
            chatState.getObjectByKey({ type: "artifact", id });
        }
        return match; // pass through; citationsPlugin handles ${A:...} directly
    });

    // eslint-disable-next-line no-useless-escape
    const rEf = /\$\{E:([0-9]+)(?:,\s*[0-9]+,\s*[0-9]+)?\}/g;
    text = text.replace(rEf, (match, event_id) => {
        const id = parseInt(event_id);
        chatState.chatObjects.update([{ key: { type: "event", id }, value: { msgId } }]);
        if (chatState.chatObjects.get({ type: "event", id }) === undefined) {
            chatState.getObjectByKey({ type: "event", id });
        }
        return match; // pass through; citationsPlugin handles ${E:...} directly
    });

    // eslint-disable-next-line no-useless-escape
    const rDf = /\$\{D:([0-9]+)(?:,\s*[0-9]+,\s*[0-9]+)?\}/g;
    text = text.replace(rDf, (match, document_id) => {
        const id = parseInt(document_id);
        chatState.chatObjects.update([{ key: { type: "document", id }, value: { msgId } }]);
        if (chatState.chatObjects.get({ type: "document", id }) === undefined) {
            chatState.getObjectByKey({ type: "document", id });
        }
        return match; // pass through; citationsPlugin handles ${D:...} directly
    });

    // LEGACY: Handle ${artifact_id, start_line, end_line, cited_text} citations
    text = text.replace(/\$\{([0-9]+),\s*([0-9]+),\s*([0-9]+),\s*(.+?)\}/g, (match, artifact_id, start_line, end_line, cited_text) => {
        const id = parseInt(artifact_id);
        if (chatState.chatObjects.get({ type: "artifact", id }) === undefined) {

            chatState.chatObjects.update([{ key: { type: "artifact", id }, value: { msgId } }]);
            chatState.getObjectByKey({ type: "artifact", id });
        }
        const citedTextRef = storeCitedText(cited_text || "");
        return `[cite:artifact:${id}:${start_line}:${end_line}:${citedTextRef}]`;
    });

    return text;
}

const CITATION_OBJECT_TYPES = { A: "artifact", E: "event", D: "document" } as const;

/** Load the objects a turn's registered citations point to, so a quote resolved from its label shows its source. */
function preloadRegisteredCitations(citations: CitationRegistry) {
    for (const prefix of ["A", "E", "D"] as const) {
        for (const cite of citations[prefix].values()) {
            const key = { type: CITATION_OBJECT_TYPES[prefix], id: cite.id };
            if (chatState.chatObjects.get(key) !== undefined) continue;
            chatState.chatObjects.update([{ key, value: {} }]);
            chatState.getObjectByKey(key);
        }
    }
}


/**
 * One markdown block of a message. Its editor lives through streaming and the commit of the
 * final message; its text is updated in place, since remounting the editor blanks it for a few frames.
 */
const ChatTextBlock = memo(({ contentClass, text }: { contentClass: string, text: string }) => {
    const editorRef = useRef<MDXEditorMethods>(null);
    // The editor reads `markdown` when it mounts, and again whenever that prop changes.
    // Passing the live stream rebuilds it on every token and trips a ResizeObserver loop.
    const initialText = useRef(text);
    const shownRef = useRef(text);
    useEffect(() => {
        if (!editorRef.current || shownRef.current === text) return;
        editorRef.current.setMarkdown(text);
        shownRef.current = text;
    }, [text]);
    return <TextMessageDisplay ref={editorRef} contentClass={contentClass} content={initialText.current} />;
});

/**
 * Message text, streaming or final. `setMarkdown` recreates every node of an editor, citations
 * included, so each top-level block gets its own editor and only the growing last block is re-imported.
 */
const ChatText = observer(({ contentClass, content, streaming, split, citations }: { contentClass: string, content: string, streaming?: boolean, split?: boolean, citations?: CitationRegistry }) => {
    // Held after the citation closes: its placeholder shows until the block's editor re-imports it.
    const streamingQuote = useRef("");
    useEffect(() => {
        if (citations) preloadRegisteredCitations(citations);
    }, [citations]);
    const raw = (streaming ? chatState.streamingContent : content).replace(/```/g, "");
    // Resolved the way the backend bakes the saved message, so its blocks match the streamed ones.
    const text = citations ? resolveCitationLabels(raw, citations) : raw;
    const blocks = split ? splitMarkdownBlocks(text) : [text];
    if (streaming && blocks.length > 0) {
        const last = citations ? trimPartialLabelTail(blocks[blocks.length - 1], citations) : blocks[blocks.length - 1];
        const tail = streamingCitationTail(last);
        blocks[blocks.length - 1] = tail.text;
        if (tail.quote !== null) streamingQuote.current = tail.quote;
    }
    if (blocks.length === 0) blocks.push("");
    return (
        <StreamingQuoteContext.Provider value={streamingQuote.current}>
            <div className={`chat-message ${contentClass}`}>
                {blocks.map((block, i) => (
                    <div key={i} className="chat-text-block">
                        <ChatTextBlock contentClass={contentClass} text={block} />
                    </div>
                ))}
            </div>
        </StreamingQuoteContext.Provider>
    );
});

export const MessageContent = memo(({ msg, MkMessageClass, citations }: {
    msg: ChatMessage,
    MkMessageClass: (msg: ChatMessage) => string,
    /** Citations registered in this message's turn, which its text refers to by label. */
    citations?: CitationRegistry
}) => {


    // Memoize the compact result for this specific message content
    const content_compacted = useMemo(() => {
        if (!Array.isArray(msg.content)) return null;

        const ret = [];
        let cur_type = "";

        for (const item of msg.content) {
            if (item.type === "tool_result" || item.type === "user_action" || item.type === "tool_use" || item.type === "skill") {
                ret.push({ ...item });
            } else {
                let processedText = processTextCitations(item.text || "", msg.id);

                // Handle citations if present - append all citations at the end of the text block - LEGACY SUPPORT FOR CITATIONS 
                // TODO remove legacy support after transition period
                if (item.citations && item.citations.length > 0) {
                    for (const citation of item.citations) {
                        if (citation.type == "char_location" && citation?.document_title?.tool_use_id) {
                            const citedTextRef = storeCitedText(citation.cited_text || "");
                            const citationMarker = `[cite:${citation.document_title.tool_use_id}:${citation.document_title.id}:${citation.document_title.name}:${citation.document_title.type}:${citedTextRef}]`;
                            processedText += citationMarker;
                        } else if (citation.type == "artifact_location") {
                            chatState.chatObjects.update([{ key: { type: "artifact", id: citation.artifact_id }, value: { msgId: msg.id } }])
                            chatState.getObjectByKey({ type: "artifact", id: citation.artifact_id })

                            const citedTextRef = storeCitedText(citation.cited_text || "");
                            const citationMarker = `[cite:artifact:${citation.artifact_id}:${citation.start_line}:${citation.end_line}:${citedTextRef}]`;
                            processedText += citationMarker;
                        }
                    }
                }

                // Create the processed item
                const processedItem = {
                    ...item,
                    text: processedText
                };

                if (cur_type !== item.type) {
                    ret.push(processedItem);
                } else if (cur_type === "text") {
                    (ret[ret.length - 1] as ChatMessageBlockText).text += processedItem.text;
                }
            }
            cur_type = item.type;
        }
        return ret;
    }, [msg.content]);

    // User messages keep a single editor, so a tinted bubble (pre-prompt, identity) stays one box.
    const splitText = msg.role === "assistant";

    // Streaming, string and block messages share one tree shape (column > keyed rows), so the
    // committed message reuses the streaming placeholder's components instead of remounting them.
    if (msg.incomplete) {
        if (!msg.content || typeof msg.content === 'string')
            return

        const entry = msg.content.find((e) => e.type === "text" || e.type === "tool_use");
        if (!entry) return null;
        return (
            <div className="flex flex-col w-full">
                {[
                    <div key={0} className={`w-full flex flex-row justify-center`}>
                        {entry.type === "text"
                            ? <ChatText contentClass={MkMessageClass(msg)} content={entry.text ?? ""} streaming={!entry.text} split={splitText} citations={citations} />
                            : <ToolMessageDisplay msg={msg} entry={entry} streaming />}
                    </div>,
                ]}
            </div>
        );
    }

    if (typeof msg.content === 'string') {
        const processed = processTextCitations(msg.content, msg.id);
        return (
            <div className="flex flex-col w-full">
                {[
                    <div key={0} className={`w-full flex flex-row justify-center`}>
                        <ChatText contentClass={MkMessageClass(msg)} content={processed} split={splitText} citations={citations} />
                    </div>,
                ]}
            </div>
        );
    }
    if (Array.isArray(msg.content) && content_compacted) {
        return (
            <div className="flex flex-col w-full">
                {content_compacted.map((entry, i) => {
                    switch (entry.type) {
                        case "text":
                            return (
                                <div key={i} className={`w-full flex flex-row justify-center`}>
                                    <ChatText
                                        contentClass={MkMessageClass(msg)}
                                        content={entry.text}
                                        split={splitText}
                                        citations={citations}
                                    />
                                </div>
                            );
                        case "tool_use":
                            return (
                                <div key={i} className={`w-full flex flex-row justify-center`}>
                                    <ToolMessageDisplay msg={msg} entry={entry} partial_tool_input={JSON.stringify(entry.input ?? {})} />
                                </div>
                            );
                        case "user_action":
                            if (entry.action === "artifact_variable_name_update") return null;
                            return (
                                <div key={i} className={`w-full flex flex-row justify-center`}>
                                    <ToolMessageDisplay msg={msg} entry={entry} />
                                </div>
                            );
                        case "skill":
                            return (
                                <div key={i} className="w-full flex flex-row justify-center">
                                    <SkillChip skillKey={entry.skill_key} label={entry.label} />
                                </div>
                            );
                        default:
                            return null;
                    }
                })}
            </div>
        );
    }

    return null;
});
